import { Modal, VStack, HStack } from '@musinsa/mwms-ds'
import { Controller, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import Input from '@/components/content/Input'
import Text from '@/components/content/Text'
import { Select } from '@/components/content/Select'
import { DatePickerInput } from '@/components/content/DatePicker/DatePickerInput'
import { colorSystem } from '@/styles/theme'
import { DATE_FORMAT } from '@/utils/consts'
import { Utils } from '@/utils/Utils'
import MToast from '@/utils/modal/MToast'
import MAlert from '@/utils/modal/MAlert'
import { useGetPriceCodes, useCreateCategoryPrice, useUpdateCategoryPrice } from '@/services/settlement/settlementPricing/useSettlementPricingQueries'
import { useGetCountries, useGetLogisticsCategories } from '@/services/commonCode/useCommonCodeQueries'
import { ORDER_TYPE_OPTIONS, UNIT_REGISTER_OPTIONS } from '../../consts'
import { categoryPriceFormSchema, CategoryPriceFormSchemaType, getInitForm } from '../../schemas/categoryPriceFormSchema'

interface Props {
  open: boolean
  defaultValues: (CategoryPriceFormSchemaType & { id: number }) | null
  refetch: () => void
  onClose: () => void
}

const isEdit = (d: Props['defaultValues']): d is CategoryPriceFormSchemaType & { id: number } => d !== null && d.id !== undefined

// label 좌측 · control 우측 가로 배치 (Figma/HTML 목업), 필수표시 * 파란색
function Field({ label, required = true, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <HStack gap={16} align="center">
      <HStack gap={2} align="center" style={{ width: 110, flexShrink: 0 }}>
        <Text kind="Body_14_medium" color={colorSystem.text.common.primary}>
          {label}
        </Text>
        {required && (
          <Text kind="Body_14_medium" color="#245EFF">
            *
          </Text>
        )}
      </HStack>
      <VStack gap={4} style={{ flex: 1, minWidth: 0 }}>
        {children}
      </VStack>
    </HStack>
  )
}

function Err({ msg }: { msg?: string }) {
  if (!msg) return null
  return (
    <Text kind="Body_12_medium" color={colorSystem.text.common.error}>
      {msg}
    </Text>
  )
}

export function CategoryPriceFormModal({ open, defaultValues, refetch, onClose }: Props) {
  const edit = isEdit(defaultValues)
  const { data: codes } = useGetPriceCodes()
  const { data: countries } = useGetCountries() // 국가: moms BE 원천
  const { data: logisticsCategories } = useGetLogisticsCategories() // 물류카테고리(category####): moms BE 원천
  const create = useCreateCategoryPrice()
  const update = useUpdateCategoryPrice()

  const form = useForm<CategoryPriceFormSchemaType>({
    resolver: zodResolver(categoryPriceFormSchema),
    defaultValues: getInitForm(defaultValues ?? {}),
    mode: 'onChange',
  })
  const {
    control,
    watch,
    handleSubmit,
    reset,
    formState: { isValid },
  } = form
  const values = watch()

  useEffect(() => {
    if (!open) return
    // useForm defaultValues 는 최초 마운트에만 적용된다. 같은 행 재수정은 key(id)가 동일해
    // 리마운트되지 않고 닫을 때 빈값으로 reset 된 폼이 그대로 남으므로, 열릴 때마다 재초기화. (LOGISPRD-9874)
    reset(getInitForm(defaultValues ?? {}))
    if (edit) form.trigger()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open])

  const close = () => {
    reset(getInitForm())
    onClose()
  }

  const onSubmit = async (f: CategoryPriceFormSchemaType) => {
    try {
      if (edit) {
        await update.mutateAsync({ id: defaultValues.id, unitPrice: Number(f.unitPrice), startDate: f.startDate, endDate: f.endDate })
        MToast.show('단가가 수정되었습니다')
      } else {
        await create.mutateAsync({
          country: f.country,
          serviceType: f.serviceType,
          orderType: f.orderType as 'DOMESTIC' | 'GLOBAL',
          unitType: f.unitType, // 등록 폼 단위 선택 (PCS/BOX) — LOGISPRD-9632
          logisticsCategoryCode: f.logisticsCategoryCode,
          unitPrice: Number(f.unitPrice),
          startDate: f.startDate,
          endDate: f.endDate,
        })
        MToast.show('단가 등록이 완료되었습니다')
      }
      reset(getInitForm())
      await refetch()
      onClose()
    } catch (e) {
      MAlert.show(Utils.getApiErrMessage(e, '단가 등록/수정에 실패했습니다.'))
    }
  }

  return (
    <Modal
      open={open}
      customWidth={480}
      title={edit ? '카테고리 단가 수정' : '카테고리 단가 등록'}
      buttons={[
        { kind: 'secondary', content: '취소', onClick: close },
        { kind: 'primary_light', content: edit ? '수정' : '등록', disabled: !isValid, onClick: handleSubmit(onSubmit) },
      ]}
      onClose={close}
    >
      <VStack gap={20} style={{ width: '100%' }}>
        <Field label="국가">
          <Controller
            control={control}
            name="country"
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="선택"
                  disabled={edit}
                  options={Utils.createSelectOptions(countries ?? [], 'name', 'code')}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="물류카테고리">
          <Controller
            control={control}
            name="logisticsCategoryCode"
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="선택"
                  disabled={edit}
                  searchable
                  searchPlaceholder="카테고리 검색"
                  optionMaxHeight={300}
                  options={Utils.createSelectOptions(logisticsCategories ?? [], 'code', 'code')}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="서비스 이용항목">
          <Controller
            control={control}
            name="serviceType"
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="선택"
                  disabled={edit}
                  options={Utils.createSelectOptions(codes?.serviceTypes ?? [], 'name', 'code')}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="주문유형">
          <Controller
            control={control}
            name="orderType"
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="선택"
                  disabled={edit}
                  options={ORDER_TYPE_OPTIONS}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="단위">
          <Controller
            control={control}
            name="unitType"
            render={({ field, fieldState: { error } }) => (
              <>
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  placeholder="선택"
                  disabled={edit}
                  options={UNIT_REGISTER_OPTIONS}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="단가">
          <Controller
            control={control}
            name="unitPrice"
            render={({ field, fieldState: { error } }) => (
              <>
                <Input
                  block
                  size="lg"
                  value={field.value}
                  onChange={(v) => field.onChange(v.replace(/[^0-9]/g, ''))}
                  placeholder="단가를 입력해 주세요."
                  unitLabel="원"
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="시작일">
          <Controller
            control={control}
            name="startDate"
            render={({ field, fieldState: { error } }) => (
              <>
                <DatePickerInput
                  {...field}
                  minDate={dayjs().toDate()}
                  maxDate={values.endDate ? dayjs(values.endDate).toDate() : undefined}
                  fixedCalendar
                  placement="bottom"
                  numericOnly
                  onChange={(d) => field.onChange(d ? dayjs(d).format(DATE_FORMAT.date) : '')}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>

        <Field label="종료일">
          <Controller
            control={control}
            name="endDate"
            render={({ field, fieldState: { error } }) => (
              <>
                <DatePickerInput
                  {...field}
                  minDate={values.startDate ? dayjs(values.startDate).add(1, 'day').toDate() : dayjs().add(1, 'day').toDate()}
                  fixedCalendar
                  placement="bottom"
                  numericOnly
                  onChange={(d) => field.onChange(d ? dayjs(d).format(DATE_FORMAT.date) : '')}
                />
                <Err msg={error?.message} />
              </>
            )}
          />
        </Field>
      </VStack>
    </Modal>
  )
}
